import React, { useState, useMemo } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { ChevronRight, Plus, Trash2 } from "lucide-react";
import { fmtM, resolveF } from "../shared/constants";
import { getPeriodDateRange, getPeriodTarget } from "../shared/components";

// ── Helpers ───────────────────────────────────────────────────────────────────
const pct    = (v, d=0) => v==null ? "—" : `${(+(v||0)*100).toFixed(d)}%`;
const clrR   = (a,t) => { if(!t||a==null) return "#888"; const r=a/t; return r>=0.95?"#16A34A":r>=0.75?"#D97706":"#DC2626"; };
const dot    = (a,t,hi=true) => { if(!t||a==null) return "⚪"; const r=a/t; return hi?(r>=0.95?"🟢":r>=0.75?"🟡":"🔴"):(r<=1.05?"🟢":r<=1.25?"🟡":"🔴"); };
const MONTH_KEYS   = ["jan","feb","mar","apr","may","jun","jul","aug","sep","oct","nov","dec"];
const MONTH_LABELS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const PERIODS = [
  {id:"ytd",l:"YTD"},{id:"q1",l:"Q1"},{id:"q2",l:"Q2"},{id:"q3",l:"Q3"},{id:"q4",l:"Q4"},{id:"fullyear",l:"Full Year"},
];
const impactColor  = v => v==="CRITICAL"?"#DC2626":v==="HIGH"?"#D97706":v==="MED"?"#7C3AED":"#16A34A";
const statusColor  = v => v==="Complete"?"#16A34A":v==="In Progress"?"#CC1A1A":v==="At Risk"?"#DC2626":"#888";

// ── Sub-components ────────────────────────────────────────────────────────────
const Card = ({children, style={}}) => (
  <div style={{background:"#fff",borderRadius:12,border:"1px solid rgba(0,0,0,0.07)",overflow:"hidden",marginBottom:20,...style}}>
    {children}
  </div>
);
const CardHd = ({title, sub, right}) => (
  <div style={{padding:"14px 20px",borderBottom:"1px solid rgba(0,0,0,0.06)",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
    <div>
      <div style={{fontSize:11,fontWeight:800,color:"#111",textTransform:"uppercase",letterSpacing:"0.06em"}}>{title}</div>
      {sub && <div style={{fontSize:11,color:"#888",marginTop:2}}>{sub}</div>}
    </div>
    {right}
  </div>
);
const MiniBar = ({val, max, color="#CC1A1A", height=18}) => (
  <div style={{flex:1,height,background:"rgba(0,0,0,0.06)",borderRadius:3,overflow:"hidden"}}>
    <div style={{width:`${Math.min(100,max>0?val/max*100:0)}%`,height:"100%",background:color,borderRadius:3}}/>
  </div>
);

// ═══════════════════════════════════════════════════════════════════════════════
// Main Component
// ═══════════════════════════════════════════════════════════════════════════════
const PlanOfRecord = ({ settings, hsData, onGo, onSave }) => {
  const [period, setPeriod]  = useState("ytd");
  const now    = new Date();
  const fy     = +(settings?.company?.fiscalYear || now.getFullYear());

  const rawDeals   = hsData?.rawDeals    || [];
  const wonDeals   = hsData?.rawWonDeals || [];
  const F          = resolveF(settings);
  const reps       = (settings?.team?.reps || []).filter(r => r.name);
  const linkedReps = reps.filter(r => r.hubspotOwnerId);

  // ── Plan targets ─────────────────────────────────────────────────────────
  const annualTarget   = +(settings?.revenue?.target || 0);
  const monthlyTargets = settings?.revenue?.monthlyTargets || {};
  const planWR         = +(settings?.targets?.winRate || 0);
  const planDS         = +(settings?.targets?.avgDealSize || 0);
  const planCoverage   = +(settings?.targets?.coverageRatio || 3);
  const planCycle      = +(settings?.targets?.salesCycle || 0);
  const nReps          = reps.length || 1;
  const WRd            = planWR / 100;

  // Capacity math
  const dealsNeeded    = planDS > 0 && WRd > 0 ? Math.ceil(annualTarget / planDS) : null;
  const oppsNeeded     = dealsNeeded && WRd > 0 ? Math.ceil(dealsNeeded / WRd) : null;
  const pipelineNeeded = annualTarget > 0 && WRd > 0 ? annualTarget / WRd : null;
  const quotaPerRep    = annualTarget > 0 ? annualTarget / nReps : null;
  const dealsPerRep    = dealsNeeded ? Math.ceil(dealsNeeded / nReps) : null;
  const oppsPerRep     = oppsNeeded  ? Math.ceil(oppsNeeded  / nReps) : null;
  const pipePerRep     = pipelineNeeded ? pipelineNeeded / nReps : null;

  // ── Priorities ────────────────────────────────────────────────────────────
  const [priorities, setPriorities] = useState(settings?.por?.priorities || [
    { n:"01", area:"", why:"", impact:"CRITICAL", timeline:"0–30 days",  owner:"", status:"Not Started" },
    { n:"02", area:"", why:"", impact:"HIGH",     timeline:"0–30 days",  owner:"", status:"Not Started" },
    { n:"03", area:"", why:"", impact:"HIGH",     timeline:"30–60 days", owner:"", status:"Not Started" },
  ]);
  const [saved, setSaved] = useState(false);
  const savePriorities = () => {
    onSave({ ...settings, por: { ...settings.por, priorities, configured: true } });
    setSaved(true); setTimeout(() => setSaved(false), 2000);
  };
  const updP = (i, field, val) => { const p=[...priorities]; p[i]={...p[i],[field]:val}; setPriorities(p); };

  // ── Stage mappings ────────────────────────────────────────────────────────
  const mappings    = settings?.stages?.mappings || [];
  const wonIds      = mappings.filter(m=>m.type==="won").map(m=>m.id.toLowerCase());
  const lostIds     = mappings.filter(m=>m.type==="lost").map(m=>m.id.toLowerCase());
  const hasMappings = mappings.length > 0;
  const isWon  = d => { const s=(d.properties?.[F.dealstage]||"").toLowerCase(); return wonIds.length?wonIds.includes(s):s.includes("closedwon"); };
  const isLost = d => { const s=(d.properties?.[F.dealstage]||"").toLowerCase(); return lostIds.length?lostIds.includes(s):s.includes("closedlost"); };
  const isOpen = d => !isWon(d)&&!isLost(d);
  const amt    = d => +(d.properties?.[F.amount]||0);
  const closeD = d => d.properties?.[F.closedate];
  const createD= d => d.properties?.[F.createdate]||d.properties?.createdate;
  const ownId  = d => String(d.properties?.[F.owner]||d.properties?.hubspot_owner_id||"");
  const stageV = d => (d.properties?.[F.dealstage]||"").toLowerCase();

  // ── Period ────────────────────────────────────────────────────────────────
  const todayStr   = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,"0")}-${String(now.getDate()).padStart(2,"0")}`;
  const periodRange = period==="ytd"
    ? {from:`${fy}-01-01`,to:todayStr}
    : period==="fullyear"
    ? {from:`${fy}-01-01`,to:`${fy}-12-31`}
    : getPeriodDateRange(period,fy);
  const periodFrom = new Date(periodRange.from).getTime();
  const periodTo   = new Date(periodRange.to+"T23:59:59").getTime();
  const periodTarget = period==="fullyear" ? annualTarget : period==="ytd"
    ? (()=>{ const cm=now.getMonth(); let t=0; for(let m=0;m<cm;m++) t+=+monthlyTargets[MONTH_KEYS[m]]||annualTarget/12; const mt=+monthlyTargets[MONTH_KEYS[cm]]||annualTarget/12; t+=mt*(now.getDate()/new Date(now.getFullYear(),cm+1,0).getDate()); return t; })()
    : getPeriodTarget(period,annualTarget,monthlyTargets);
  const isFuture = p => { if(p==="ytd"||p==="fullyear") return false; try { const r=getPeriodDateRange(p,fy); return new Date(r.from).getTime()>now.getTime(); } catch(e){ return false; } };

  // ── Base deal sets ────────────────────────────────────────────────────────
  const baseDeals = useMemo(() => {
    const m=new Map();
    [...rawDeals,...wonDeals].forEach(d=>{ if(!m.has(d.id)) m.set(d.id,d); });
    const all=Array.from(m.values());
    const allWon  = hasMappings?all.filter(isWon):wonDeals;
    const allLost = all.filter(isLost);
    const open    = rawDeals.filter(isOpen);
    const cacheTs = (deals, getDate) => deals.map(d=>({d, ts:getDate(d)?new Date(getDate(d)).getTime():0}));
    return { allWon, allLost, open, all,
      wonWithTs:cacheTs(allWon,closeD), lostWithTs:cacheTs(allLost,closeD),
      allWithCreateTs:cacheTs(all,createD) };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[rawDeals,wonDeals,hasMappings]);

  const {allWon, allLost, open, wonWithTs, lostWithTs, allWithCreateTs} = baseDeals;
  const hasData = rawDeals.length > 0 || wonDeals.length > 0;

  // ── Period metrics ────────────────────────────────────────────────────────
  const periodWon  = useMemo(()=>wonWithTs.filter(({ts})=>ts>=periodFrom&&ts<=periodTo).map(({d})=>d),[wonWithTs,periodFrom,periodTo]);
  const periodLost = useMemo(()=>lostWithTs.filter(({ts})=>ts>=periodFrom&&ts<=periodTo).map(({d})=>d),[lostWithTs,periodFrom,periodTo]);
  const revenue    = periodWon.reduce((a,d)=>a+amt(d),0);
  const pipeline   = open.reduce((a,d)=>a+amt(d),0);
  const totClosed  = periodWon.length+periodLost.length;
  const actualWR   = totClosed>0?periodWon.length/totClosed:null;
  const actualDS   = periodWon.length>0?revenue/periodWon.length:null;
  const coverage   = periodTarget>0?pipeline/periodTarget:null;

  // ── FY won (for Live vs Plan) ─────────────────────────────────────────────
  const fyStart = new Date(fy,0,1).getTime();
  const fyWon   = useMemo(()=>allWon.filter(d=>closeD(d)&&new Date(closeD(d)).getTime()>=fyStart),[allWon,fyStart]);
  const liveRev = fyWon.reduce((a,d)=>a+amt(d),0);
  const livePipe= pipeline;
  const fyLost  = allLost.filter(d=>closeD(d)&&new Date(closeD(d)).getTime()>=fyStart);
  const fyTotCl = fyWon.length+fyLost.length;
  const liveWR  = fyTotCl>0?fyWon.length/fyTotCl:0;
  const liveDS  = fyWon.length>0?liveRev/fyWon.length:0;
  const liveCov = annualTarget>0?livePipe/annualTarget:0;

  // ── Monthly pacing ────────────────────────────────────────────────────────
  const monthlyPacing = useMemo(() => {
    const cm = now.getMonth();
    return MONTH_KEYS.map((key, mi) => {
      const mStart = new Date(fy, mi, 1).getTime();
      const mEnd   = new Date(fy, mi+1, 0, 23, 59, 59).getTime();
      const mWon   = wonWithTs.filter(({ts})=>ts>=mStart&&ts<=mEnd).map(({d})=>d);
      const mTarget= +monthlyTargets[key] || annualTarget/12;
      const mRev   = mWon.reduce((a,d)=>a+amt(d),0);
      const isCurrent = mi === cm && now.getFullYear() === fy;
      const isPast    = mi < cm  || (mi === cm && now.getFullYear() > fy);
      const isFut     = mi > cm  || now.getFullYear() < fy;
      return { label:MONTH_LABELS[mi], target:mTarget, actual:mRev, gap:mRev-mTarget,
        att: mTarget>0?mRev/mTarget:null, isCurrent, isPast, isFut };
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[wonWithTs, monthlyTargets, annualTarget, fy]);

  // YTD pacing
  const ytdPacing = useMemo(() => {
    const cm = now.getMonth();
    let ytdActual=0, ytdTarget=0;
    for(let m=0; m<=cm; m++) {
      const mStart = new Date(fy,m,1).getTime();
      const mEnd   = new Date(fy,m+1,0,23,59,59).getTime();
      const mWon   = wonWithTs.filter(({ts})=>ts>=mStart&&ts<=mEnd);
      ytdActual += mWon.reduce((a,{d})=>a+amt(d),0);
      if(m < cm) ytdTarget += +monthlyTargets[MONTH_KEYS[m]]||annualTarget/12;
      else { const mT=+monthlyTargets[MONTH_KEYS[m]]||annualTarget/12; ytdTarget += mT*(now.getDate()/new Date(fy,m+1,0).getDate()); }
    }
    return { ytdActual, ytdTarget, att: ytdTarget>0?ytdActual/ytdTarget:null };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[wonWithTs,monthlyTargets,annualTarget,fy]);

  // ── Trend ─────────────────────────────────────────────────────────────────
  const trendMonths = useMemo(()=>{
    const months=[];
    for(let i=5;i>=0;i--){
      const d=new Date(now.getFullYear(),now.getMonth()-i,1);
      const mStart=d.getTime(), mEnd=new Date(d.getFullYear(),d.getMonth()+1,0,23,59,59).getTime();
      const mWon  =wonWithTs.filter(({ts})=>ts>=mStart&&ts<=mEnd).map(({d})=>d);
      const mLost =lostWithTs.filter(({ts})=>ts>=mStart&&ts<=mEnd).map(({d})=>d);
      const mNew  =allWithCreateTs.filter(({ts})=>ts>=mStart&&ts<=mEnd);
      const mClosed=mWon.length+mLost.length;
      const mRev  =mWon.reduce((a,x)=>a+amt(x),0);
      months.push({label:MONTH_LABELS[d.getMonth()],rev:mRev,won:mWon.length,
        newOpps:mNew.length, wr:mClosed>0?mWon.length/mClosed:null,
        avgDS:mWon.length>0?mRev/mWon.length:null });
    }
    return months;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[wonWithTs,lostWithTs,allWithCreateTs]);

  // ── Win rate by bucket ────────────────────────────────────────────────────
  const DS_BUCKETS = [
    {label:"< $10K",min:0,max:10000},{label:"$10–50K",min:10000,max:50000},
    {label:"$50–150K",min:50000,max:150000},{label:"$150K+",min:150000,max:Infinity},
  ];
  const wrByBucket = useMemo(()=>DS_BUCKETS.map(b=>{
    const bWon=periodWon.filter(d=>amt(d)>=b.min&&amt(d)<b.max);
    const bLost=periodLost.filter(d=>amt(d)>=b.min&&amt(d)<b.max);
    const tot=bWon.length+bLost.length;
    return {...b,won:bWon.length,lost:bLost.length,total:tot,wr:tot>0?bWon.length/tot:null,rev:bWon.reduce((a,d)=>a+amt(d),0)};
  }),[periodWon,periodLost]);

  // ── Stage funnel ──────────────────────────────────────────────────────────
  const stageFunnel = useMemo(()=>{
    const stageMap={};
    open.forEach(d=>{
      const s=stageV(d);
      const label=mappings.find(m=>m.id.toLowerCase()===s)?.label||s||"Unknown";
      if(!stageMap[s]) stageMap[s]={label,count:0,value:0,stalled:0};
      stageMap[s].count++; stageMap[s].value+=amt(d);
      const lastAct=d.properties?.notes_last_activity_date;
      if(!lastAct||(now.getTime()-new Date(lastAct).getTime())/86400000>30) stageMap[s].stalled++;
    });
    return Object.values(stageMap).sort((a,b)=>b.value-a.value).slice(0,8);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[open,mappings]);

  // ── Rep diagnostics ───────────────────────────────────────────────────────
  const repData = useMemo(()=>linkedReps.map(rep=>{
    const oid=String(rep.hubspotOwnerId), quota=+(rep.quota||0);
    const myWon=periodWon.filter(d=>ownId(d)===oid);
    const myLost=periodLost.filter(d=>ownId(d)===oid);
    const myOpen=open.filter(d=>ownId(d)===oid);
    const rev=myWon.reduce((a,d)=>a+amt(d),0), pipe=myOpen.reduce((a,d)=>a+amt(d),0);
    const totCl=myWon.length+myLost.length;
    const wr=totCl>0?myWon.length/totCl:null, ds=myWon.length>0?rev/myWon.length:null;
    const cov=quota>0?pipe/quota:null, att=quota>0?rev/quota:null;
    const stalled=myOpen.filter(d=>{
      const lastAct=d.properties?.notes_last_activity_date;
      return !lastAct||(now.getTime()-new Date(lastAct).getTime())/86400000>30;
    }).length;
    return {name:rep.name,oid,quota,rev,pipe,wr,ds,cov,att,stalled,openCount:myOpen.length,wonCount:myWon.length};
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }),[linkedReps,periodWon,periodLost,open]);

  const maxRev  = Math.max(...trendMonths.map(m=>m.rev),1);
  const maxOpps = Math.max(...trendMonths.map(m=>m.newOpps),1);
  const statusOpts   = ["Not Started","In Progress","Complete","At Risk","Blocked"];
  const impactOpts   = ["CRITICAL","HIGH","MED","LOW"];
  const timelineOpts = ["0–30 days","30–60 days","60–90 days","This Quarter","Next Quarter","Ongoing"];

  const PeriodBtn = ({p}) => {
    const active=period===p.id, future=isFuture(p.id);
    return <button onClick={()=>!future&&setPeriod(p.id)} disabled={future}
      style={{padding:"4px 10px",borderRadius:6,border:`1px solid ${active?"#CC1A1A":"rgba(0,0,0,0.08)"}`,
        background:active?"rgba(204,26,26,0.08)":"#fff",color:future?"#ccc":active?"#CC1A1A":"#555",
        fontSize:11,fontWeight:active?700:400,cursor:future?"not-allowed":"pointer",opacity:future?0.5:1}}>
      {p.l}
    </button>;
  };

  return (
    <div style={{maxWidth:1140,margin:"0 auto",paddingBottom:60}}>

      {/* Period selector */}
      <div style={{display:"flex",gap:6,marginBottom:20}}>
        {PERIODS.map(p=><PeriodBtn key={p.id} p={p}/>)}
      </div>

      {/* ── 1. LIVE VS PLAN ────────────────────────────────────────────────── */}
      <Card>
        <CardHd title="Live vs Plan" sub="Your committed plan vs what HubSpot shows right now"/>
        <div style={{display:"grid",gridTemplateColumns:"2fr 1fr 1fr 1fr 50px",background:"#F8F8F8",
          padding:"7px 20px",borderBottom:"1px solid rgba(0,0,0,0.05)"}}>
          {["Metric","Your Plan","Live (HubSpot)","Gap",""].map((h,i)=>(
            <div key={i} style={{fontSize:10,fontWeight:700,color:"#888",textTransform:"uppercase",
              letterSpacing:"0.06em",textAlign:i>0?"center":"left"}}>{h}</div>
          ))}
        </div>
        {[
          { label:"Revenue (FY)",      planV:annualTarget?fmtM(annualTarget):"—", liveV:hasData?fmtM(liveRev):"—",        gap:annualTarget&&hasData?fmtM(liveRev-annualTarget):"—",           d:dot(liveRev,annualTarget),          hi:true },
          { label:"Win Rate",          planV:planWR?`${planWR}%`:"—",             liveV:fyTotCl>0?`${Math.round(liveWR*100)}%`:"—",   gap:planWR&&fyTotCl>0?`${Math.round(liveWR*100-planWR)}pp`:"—",   d:dot(liveWR*100,planWR),             hi:true },
          { label:"Avg Deal Size",     planV:planDS?fmtM(planDS):"—",             liveV:liveDS>0?fmtM(liveDS):"—",        gap:planDS&&liveDS?fmtM(liveDS-planDS):"—",                            d:dot(liveDS,planDS),                 hi:true },
          { label:"Open Pipeline",     planV:pipelineNeeded?fmtM(pipelineNeeded):"—", liveV:hasData?fmtM(livePipe):"—",   gap:pipelineNeeded&&hasData?fmtM(livePipe-pipelineNeeded):"—",         d:dot(livePipe,pipelineNeeded),       hi:true },
          { label:"Pipeline Coverage", planV:`${planCoverage}x`,                  liveV:liveCov>0?`${liveCov.toFixed(1)}x`:"—", gap:liveCov>0?`${(liveCov-planCoverage).toFixed(1)}x`:"—",     d:dot(liveCov,planCoverage),          hi:true },
          { label:"Deals Won (FY)",    planV:dealsNeeded||"—",                    liveV:hasData?fyWon.length:"—",          gap:dealsNeeded&&hasData?`${fyWon.length>=dealsNeeded?"+":""}${fyWon.length-dealsNeeded}`:"—", d:dot(fyWon.length,dealsNeeded), hi:true },
        ].map((row,i)=>(
          <div key={i} style={{display:"grid",gridTemplateColumns:"2fr 1fr 1fr 1fr 50px",padding:"11px 20px",
            borderBottom:"1px solid rgba(0,0,0,0.04)",background:i%2?"rgba(0,0,0,0.01)":"#fff",alignItems:"center"}}>
            <div style={{fontSize:12,fontWeight:500,color:"#333"}}>{row.label}</div>
            <div style={{fontSize:12,color:"#888",textAlign:"center",fontFamily:"'DM Mono',monospace"}}>{row.planV}</div>
            <div style={{fontSize:13,fontWeight:700,color:"#111",textAlign:"center",fontFamily:"'DM Mono',monospace"}}>{row.liveV}</div>
            <div style={{fontSize:11,fontWeight:600,textAlign:"center",fontFamily:"'DM Mono',monospace",
              color:row.gap==="—"?"#888":String(row.gap).startsWith("-")?"#DC2626":"#16A34A"}}>{row.gap}</div>
            <div style={{textAlign:"center",fontSize:14}}>{row.d}</div>
          </div>
        ))}
      </Card>

      {/* ── 2. TARGETS VS ACTUALS (monthly) ────────────────────────────────── */}
      <Card>
        <CardHd title="Targets vs Actuals" sub={`Monthly pacing against committed plan · ${fy} FY`}
          right={hasData&&ytdPacing.ytdTarget>0&&(
            <div style={{textAlign:"right",fontSize:11}}>
              <div style={{fontWeight:700,color:"#555",fontSize:10,textTransform:"uppercase",letterSpacing:"0.06em"}}>YTD Pacing</div>
              <div style={{fontWeight:800,color:clrR(ytdPacing.ytdActual,ytdPacing.ytdTarget),fontFamily:"'DM Mono',monospace"}}>
                {fmtM(ytdPacing.ytdActual)} / {fmtM(ytdPacing.ytdTarget)} target ({ytdPacing.att?`${Math.round(ytdPacing.att*100)}%`:"—"})
              </div>
            </div>
          )}
        />
        {/* Progress bar */}
        {hasData && ytdPacing.ytdTarget > 0 && (
          <div style={{padding:"8px 20px",borderBottom:"1px solid rgba(0,0,0,0.05)"}}>
            <div style={{height:6,background:"rgba(0,0,0,0.07)",borderRadius:99,overflow:"hidden"}}>
              <div style={{width:`${Math.min(100,ytdPacing.att*100||0)}%`,height:"100%",background:clrR(ytdPacing.ytdActual,ytdPacing.ytdTarget),borderRadius:99}}/>
            </div>
          </div>
        )}
        {/* Header */}
        <div style={{display:"grid",gridTemplateColumns:"100px 1fr 1fr 1fr 100px",
          background:"#F8F8F8",padding:"7px 20px",borderBottom:"1px solid rgba(0,0,0,0.05)"}}>
          {["Month","Target","Actual","Gap","Pacing"].map((h,i)=>(
            <div key={i} style={{fontSize:10,fontWeight:700,color:"#888",textTransform:"uppercase",letterSpacing:"0.06em"}}>{h}</div>
          ))}
        </div>
        {monthlyPacing.map((m,i)=>(
          <div key={i} style={{display:"grid",gridTemplateColumns:"100px 1fr 1fr 1fr 100px",
            padding:"10px 20px",borderBottom:"1px solid rgba(0,0,0,0.04)",
            background:m.isCurrent?"rgba(204,26,26,0.02)":i%2?"rgba(0,0,0,0.01)":"#fff",alignItems:"center"}}>
            <div style={{display:"flex",alignItems:"center",gap:6}}>
              <span style={{fontSize:12,fontWeight:m.isCurrent?800:500,color:m.isCurrent?"#CC1A1A":"#333"}}>{m.label}</span>
              {m.isCurrent&&<span style={{fontSize:9,background:"#CC1A1A",color:"#fff",borderRadius:4,padding:"1px 5px",fontWeight:700}}>NOW</span>}
            </div>
            <div style={{fontSize:12,color:"#888",fontFamily:"'DM Mono',monospace"}}>{fmtM(m.target)}</div>
            <div style={{fontSize:12,fontWeight:m.isPast||m.isCurrent?700:400,
              color:m.isFut?"#ccc":clrR(m.actual,m.target),fontFamily:"'DM Mono',monospace"}}>
              {m.isFut?"—":fmtM(m.actual)}
            </div>
            <div style={{fontSize:11,fontWeight:600,fontFamily:"'DM Mono',monospace",
              color:m.isFut?"#ccc":m.gap>=0?"#16A34A":"#DC2626"}}>
              {m.isFut?"—":`${m.gap>=0?"+":""}${fmtM(m.gap)}`}
            </div>
            <div style={{display:"flex",alignItems:"center",gap:8}}>
              {!m.isFut&&<MiniBar val={m.actual} max={m.target*1.2} color={clrR(m.actual,m.target)} height={5}/>}
              <span style={{fontSize:12}}>{m.isFut?"":dot(m.actual,m.target)}</span>
            </div>
          </div>
        ))}
      </Card>

      {/* ── 3. CAPACITY CHECK ──────────────────────────────────────────────── */}
      <Card>
        <CardHd title="Capacity Check" sub={`Can your team cover the number? · ${nReps} rep${nReps!==1?"s":""} configured`}/>
        <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",borderBottom:"1px solid rgba(0,0,0,0.06)"}}>
          {[
            {label:"Quota per Rep",    value:quotaPerRep?fmtM(quotaPerRep):"—",   sub:`${fmtM(annualTarget)} ÷ ${nReps} reps`},
            {label:"Deals per Rep",    value:dealsPerRep||"—",                     sub:"to hit annual quota"},
            {label:"Opps per Rep",     value:oppsPerRep||"—",                      sub:`at ${planWR||"?"}% win rate`},
            {label:"Pipeline per Rep", value:pipePerRep?fmtM(pipePerRep):"—",     sub:"required at plan WR"},
          ].map((kpi,i)=>(
            <div key={i} style={{padding:"16px 20px",borderRight:i<3?"1px solid rgba(0,0,0,0.06)":"none"}}>
              <div style={{fontSize:10,fontWeight:700,color:"#888",textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:6}}>{kpi.label}</div>
              <div style={{fontSize:22,fontWeight:800,color:"#111",fontFamily:"'DM Mono',monospace"}}>{kpi.value}</div>
              <div style={{fontSize:10,color:"#aaa",marginTop:4}}>{kpi.sub}</div>
            </div>
          ))}
        </div>
        {reps.some(r=>r.quota)&&(
          <>
            <div style={{display:"grid",gridTemplateColumns:"2fr 1fr 1fr 1fr 1fr",background:"#F8F8F8",
              padding:"7px 20px",borderBottom:"1px solid rgba(0,0,0,0.05)"}}>
              {["Rep","Quota","Deals Needed","Opps Needed","Pipeline Needed"].map((h,i)=>(
                <div key={i} style={{fontSize:10,fontWeight:700,color:"#888",textTransform:"uppercase",
                  letterSpacing:"0.06em",textAlign:i>0?"center":"left"}}>{h}</div>
              ))}
            </div>
            {reps.map((rep,i)=>{
              const repQ=+(rep.quota||0);
              const repDs=planDS&&WRd&&repQ?Math.ceil(repQ/planDS):null;
              const repOpps=repDs&&WRd?Math.ceil(repDs/WRd):null;
              const repPipe=repQ&&WRd?repQ/WRd:null;
              return (
                <div key={i} style={{display:"grid",gridTemplateColumns:"2fr 1fr 1fr 1fr 1fr",
                  padding:"10px 20px",borderBottom:"1px solid rgba(0,0,0,0.04)",
                  background:i%2?"rgba(0,0,0,0.01)":"#fff",alignItems:"center"}}>
                  <div style={{fontSize:12,fontWeight:600,color:"#111"}}>{rep.name}</div>
                  <div style={{textAlign:"center",fontSize:12,fontFamily:"'DM Mono',monospace",color:"#555"}}>{repQ?fmtM(repQ):"—"}</div>
                  <div style={{textAlign:"center",fontSize:12,fontFamily:"'DM Mono',monospace",color:"#555"}}>{repDs||"—"}</div>
                  <div style={{textAlign:"center",fontSize:12,fontFamily:"'DM Mono',monospace",color:"#555"}}>{repOpps||"—"}</div>
                  <div style={{textAlign:"center",fontSize:12,fontFamily:"'DM Mono',monospace",color:"#555"}}>{repPipe?fmtM(repPipe):"—"}</div>
                </div>
              );
            })}
          </>
        )}
      </Card>

      {/* ── 4. CHARTS ROW ──────────────────────────────────────────────────── */}
      {hasData&&(
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16,marginBottom:20}}>
          {/* Monthly bookings */}
          <Card style={{marginBottom:0}}>
            <CardHd title="Monthly Bookings" sub="Revenue closed + new opps created (last 6 months)"/>
            <div style={{padding:"16px 16px 8px"}}>
              <ResponsiveContainer width="100%" height={180}>
                <BarChart data={trendMonths} barGap={4}>
                  <XAxis dataKey="label" tick={{fontSize:10,fill:"#888"}} axisLine={false} tickLine={false}/>
                  <YAxis tick={{fontSize:10,fill:"#888"}} axisLine={false} tickLine={false} tickFormatter={v=>fmtM(v)} width={48}/>
                  <Tooltip formatter={(v,n)=>[n==="rev"?fmtM(v):v,n==="rev"?"Revenue":"New Opps"]}
                    contentStyle={{fontSize:11,borderRadius:8,border:"1px solid rgba(0,0,0,0.08)"}}/>
                  <Bar dataKey="rev" fill="#CC1A1A" radius={[3,3,0,0]}/>
                </BarChart>
              </ResponsiveContainer>
              <div style={{display:"flex",gap:16,marginTop:4}}>
                {trendMonths.map((m,i)=>(
                  <div key={i} style={{flex:1,textAlign:"center"}}>
                    <div style={{fontSize:9,color:"#aaa"}}>{m.won}W / {m.newOpps} new</div>
                  </div>
                ))}
              </div>
            </div>
          </Card>

          {/* Win rate trend */}
          <Card style={{marginBottom:0}}>
            <CardHd title="Win Rate Trend" sub="Monthly win rate + avg deal size (last 6 months)"/>
            <div style={{padding:"16px 16px 8px"}}>
              {trendMonths.map((m,i)=>(
                <div key={i} style={{display:"flex",alignItems:"center",gap:10,marginBottom:10}}>
                  <div style={{width:28,fontSize:10,color:"#888",flexShrink:0}}>{m.label}</div>
                  <MiniBar val={m.wr||0} max={1} color={m.wr!=null?clrR(m.wr,planWR/100):"#eee"}/>
                  <div style={{width:36,textAlign:"right",fontSize:11,fontWeight:700,
                    color:m.wr!=null?clrR(m.wr,planWR/100):"#ccc",fontFamily:"'DM Mono',monospace"}}>
                    {m.wr!=null?pct(m.wr):"—"}
                  </div>
                  <div style={{width:52,textAlign:"right",fontSize:10,color:"#888",fontFamily:"'DM Mono',monospace"}}>
                    {m.avgDS?fmtM(m.avgDS):"—"}
                  </div>
                </div>
              ))}
              {planWR>0&&<div style={{fontSize:10,color:"#CC1A1A",marginTop:4}}>Plan WR: {planWR}%</div>}
            </div>
          </Card>
        </div>
      )}

      {/* ── 5. WIN RATE BY DEAL SIZE + STAGE FUNNEL ────────────────────────── */}
      {hasData&&(
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16,marginBottom:20}}>
          <Card style={{marginBottom:0}}>
            <CardHd title="Win Rate by Deal Size" sub="Where are you winning and losing?"/>
            <div style={{padding:"12px 16px"}}>
              {wrByBucket.map((b,i)=>(
                <div key={i} style={{marginBottom:12}}>
                  <div style={{display:"flex",justifyContent:"space-between",marginBottom:4}}>
                    <span style={{fontSize:11,fontWeight:600,color:"#333"}}>{b.label}</span>
                    <span style={{fontSize:11,color:"#888"}}>{b.won}W / {b.lost}L{b.wr!=null?` · ${pct(b.wr)}`:""}</span>
                  </div>
                  <div style={{display:"flex",height:8,borderRadius:4,overflow:"hidden",gap:1}}>
                    <div style={{width:b.total>0?`${b.won/b.total*100}%`:"0%",background:"#16A34A"}}/>
                    <div style={{flex:1,background:"#F0F0F0"}}/>
                  </div>
                </div>
              ))}
            </div>
          </Card>

          <Card style={{marginBottom:0}}>
            <CardHd title="Open Pipeline by Stage" sub="Value + stalled deals (no activity 30+ days)"/>
            <div style={{padding:"12px 16px"}}>
              {stageFunnel.length===0&&<div style={{textAlign:"center",padding:20,color:"#aaa",fontSize:12}}>No open pipeline</div>}
              {stageFunnel.map((s,i)=>{
                const maxV=stageFunnel[0]?.value||1;
                return (
                  <div key={i} style={{marginBottom:10}}>
                    <div style={{display:"flex",justifyContent:"space-between",marginBottom:4}}>
                      <span style={{fontSize:11,fontWeight:600,color:"#333",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",maxWidth:"55%"}}>{s.label}</span>
                      <span style={{fontSize:11,color:"#888"}}>{fmtM(s.value)} · {s.count} deals{s.stalled>0?` · ${s.stalled} stalled`:""}</span>
                    </div>
                    <MiniBar val={s.value} max={maxV} color="#7C3AED" height={6}/>
                  </div>
                );
              })}
            </div>
          </Card>
        </div>
      )}

      {/* ── 6. REP DIAGNOSTICS ─────────────────────────────────────────────── */}
      {repData.length>0&&(
        <Card>
          <CardHd title="Rep Diagnostics" sub={`${PERIODS.find(p=>p.id===period)?.l||period} performance · ${linkedReps.length} rep${linkedReps.length!==1?"s":""} linked`}/>
          <div style={{display:"grid",gridTemplateColumns:"140px 1fr 80px 80px 80px 70px 60px 60px 60px",
            background:"#F8F8F8",padding:"7px 16px",borderBottom:"1px solid rgba(0,0,0,0.05)"}}>
            {["Rep","Attainment","Revenue","Quota","Pipeline","Coverage","WR","ADS","Stalled"].map((h,i)=>(
              <div key={i} style={{fontSize:9,fontWeight:700,color:"#aaa",textTransform:"uppercase",letterSpacing:"0.06em",textAlign:i>0?"center":"left"}}>{h}</div>
            ))}
          </div>
          {repData.map((r,i)=>(
            <div key={i} style={{display:"grid",gridTemplateColumns:"140px 1fr 80px 80px 80px 70px 60px 60px 60px",
              padding:"10px 16px",borderBottom:"1px solid rgba(0,0,0,0.04)",
              background:i%2?"rgba(0,0,0,0.01)":"#fff",alignItems:"center"}}>
              <div style={{fontSize:12,fontWeight:600,color:"#111",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{r.name}</div>
              <div style={{display:"flex",alignItems:"center",gap:8}}>
                <MiniBar val={r.rev} max={r.quota||1} color={clrR(r.rev,r.quota)} height={8}/>
                <span style={{fontSize:11,fontWeight:700,color:clrR(r.rev,r.quota),minWidth:36,fontFamily:"'DM Mono',monospace"}}>{r.att!=null?pct(r.att):"-"}</span>
              </div>
              <div style={{textAlign:"center",fontSize:11,fontFamily:"'DM Mono',monospace",color:"#111"}}>{r.rev>0?fmtM(r.rev):"—"}</div>
              <div style={{textAlign:"center",fontSize:11,fontFamily:"'DM Mono',monospace",color:"#888"}}>{r.quota?fmtM(r.quota):"—"}</div>
              <div style={{textAlign:"center",fontSize:11,fontFamily:"'DM Mono',monospace",color:"#7C3AED"}}>{r.pipe>0?fmtM(r.pipe):"—"}</div>
              <div style={{textAlign:"center",fontSize:11,fontFamily:"'DM Mono',monospace",color:r.cov!=null?clrR(r.cov,planCoverage/100||1):"#888"}}>{r.cov!=null?`${r.cov.toFixed(1)}x`:"—"}</div>
              <div style={{textAlign:"center",fontSize:11,fontFamily:"'DM Mono',monospace",color:r.wr!=null?clrR(r.wr,planWR/100):"#888"}}>{r.wr!=null?pct(r.wr):"-"}</div>
              <div style={{textAlign:"center",fontSize:11,fontFamily:"'DM Mono',monospace",color:"#888"}}>{r.ds?fmtM(r.ds):"—"}</div>
              <div style={{textAlign:"center",fontSize:11,color:r.stalled>0?"#DC2626":"#16A34A",fontWeight:r.stalled>0?700:400}}>{r.stalled||"✓"}</div>
            </div>
          ))}
        </Card>
      )}

      {/* ── 7. TOP PRIORITIES ──────────────────────────────────────────────── */}
      <Card>
        <CardHd title="Top Priorities" sub="Operational levers · Owner · Timeline · Live status"
          right={
            <div style={{display:"flex",alignItems:"center",gap:8}}>
              {["Not Started","In Progress","Complete","At Risk"].map(s=>{
                const count=priorities.filter(p=>p.status===s).length;
                if(!count) return null;
                const c=statusColor(s);
                return <div key={s} style={{fontSize:10,fontWeight:700,color:c,background:`${c}15`,borderRadius:5,padding:"3px 8px"}}>{count} {s}</div>;
              })}
              <button onClick={savePriorities}
                style={{padding:"6px 14px",borderRadius:7,border:"1px solid rgba(0,0,0,0.1)",
                  background:saved?"#16A34A":"#fff",color:saved?"#fff":"#CC1A1A",
                  fontSize:11,fontWeight:700,cursor:"pointer"}}>
                {saved?"✓ Saved":"Save"}
              </button>
            </div>
          }
        />
        <div style={{padding:"12px 16px"}}>
          {priorities.map((p,i)=>{
            const ic=impactColor(p.impact), sc=statusColor(p.status);
            return (
              <div key={i} style={{display:"grid",gridTemplateColumns:"36px 1fr 1fr 110px 120px 110px 120px 32px",
                gap:8,alignItems:"center",padding:"10px 12px",borderRadius:8,marginBottom:6,
                background:p.status==="Complete"?"rgba(22,163,74,0.03)":p.status==="At Risk"?"rgba(220,38,38,0.03)":"rgba(0,0,0,0.01)",
                border:`1px solid ${p.status==="Complete"?"rgba(22,163,74,0.12)":p.status==="At Risk"?"rgba(220,38,38,0.1)":"rgba(0,0,0,0.06)"}`}}>
                <div style={{width:28,height:28,borderRadius:6,background:`${ic}18`,border:`1px solid ${ic}40`,
                  display:"flex",alignItems:"center",justifyContent:"center",fontSize:10,fontWeight:800,color:ic}}>{p.n}</div>
                <input value={p.area||""} placeholder="Priority area" onChange={e=>updP(i,"area",e.target.value)}
                  style={{background:"transparent",border:"none",borderBottom:"1px solid rgba(0,0,0,0.08)",padding:"3px 0",color:"#111",fontSize:12,fontWeight:600,outline:"none"}}/>
                <input value={p.why||""} placeholder="Why it matters" onChange={e=>updP(i,"why",e.target.value)}
                  style={{background:"transparent",border:"none",borderBottom:"1px solid rgba(0,0,0,0.08)",padding:"3px 0",color:"#666",fontSize:11,outline:"none"}}/>
                <select value={p.impact} onChange={e=>updP(i,"impact",e.target.value)}
                  style={{background:`${ic}10`,border:`1px solid ${ic}30`,borderRadius:6,padding:"5px 8px",color:ic,fontSize:11,fontWeight:700,outline:"none",cursor:"pointer"}}>
                  {impactOpts.map(o=><option key={o}>{o}</option>)}
                </select>
                <select value={p.timeline} onChange={e=>updP(i,"timeline",e.target.value)}
                  style={{background:"#F8F8F8",border:"1px solid rgba(0,0,0,0.08)",borderRadius:6,padding:"5px 8px",color:"#555",fontSize:11,outline:"none",cursor:"pointer"}}>
                  {timelineOpts.map(o=><option key={o}>{o}</option>)}
                </select>
                <input value={p.owner||""} placeholder="Owner" onChange={e=>updP(i,"owner",e.target.value)}
                  style={{background:"#F8F8F8",border:"1px solid rgba(0,0,0,0.08)",borderRadius:6,padding:"5px 8px",color:"#111",fontSize:11,outline:"none"}}/>
                <select value={p.status} onChange={e=>updP(i,"status",e.target.value)}
                  style={{background:`${sc}10`,border:`1px solid ${sc}30`,borderRadius:6,padding:"5px 8px",color:sc,fontSize:11,fontWeight:600,outline:"none",cursor:"pointer"}}>
                  {statusOpts.map(o=><option key={o}>{o}</option>)}
                </select>
                <button onClick={()=>setPriorities(priorities.filter((_,j)=>j!==i))}
                  style={{background:"none",border:"none",cursor:"pointer",color:"#ccc",padding:0,display:"flex",alignItems:"center"}}
                  onMouseEnter={e=>e.currentTarget.style.color="#DC2626"}
                  onMouseLeave={e=>e.currentTarget.style.color="#ccc"}>
                  <Trash2 size={13}/>
                </button>
              </div>
            );
          })}
          <button onClick={()=>setPriorities([...priorities,{n:String(priorities.length+1).padStart(2,"0"),area:"",why:"",impact:"MED",timeline:"30–60 days",owner:"",status:"Not Started"}])}
            style={{display:"flex",alignItems:"center",gap:6,background:"transparent",border:"1px dashed rgba(0,0,0,0.12)",
              borderRadius:7,padding:"8px 14px",color:"#888",fontSize:11,cursor:"pointer",marginTop:4,width:"100%",justifyContent:"center"}}
            onMouseEnter={e=>{e.currentTarget.style.borderColor="#CC1A1A";e.currentTarget.style.color="#CC1A1A"}}
            onMouseLeave={e=>{e.currentTarget.style.borderColor="rgba(0,0,0,0.12)";e.currentTarget.style.color="#888"}}>
            <Plus size={12}/> Add Priority
          </button>
        </div>
      </Card>

    </div>
  );
};

export default PlanOfRecord;
